<template>
  <div class="alarm-analysis-page">
    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" size="small">
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="query.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd HH:mm:ss"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :picker-options="pickerOptions"
          />
        </el-form-item>
        <el-form-item label="区域">
          <el-select v-model="query.region" clearable filterable placeholder="全部区域" style="width: 180px;">
            <el-option v-for="item in regionOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="报警类型">
          <el-select v-model="query.alarmType" clearable placeholder="全部类型" style="width: 140px;">
            <el-option v-for="item in typeOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="紧急程度">
          <el-radio-group v-model="query.emergencyLevel" size="small">
            <el-radio-button label="all">全部</el-radio-button>
            <el-radio-button label="high">高危</el-radio-button>
            <el-radio-button label="medium">中危</el-radio-button>
            <el-radio-button label="low">低危</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="粒度">
          <el-select v-model="query.granularity" style="width: 120px;">
            <el-option label="按天" value="day" />
            <el-option label="按月" value="month" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadData">查询</el-button>
          <el-button @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="12" class="stat-row">
      <el-col :span="8">
        <el-card shadow="hover" class="stat-item">
          <div class="label">报警总数</div>
          <div class="value">{{ summary.totalCount }}</div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="stat-item">
          <div class="label">设备故障</div>
          <div class="value">{{ summary.deviceFaultCount }}</div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="stat-item">
          <div class="label">违规事件</div>
          <div class="value">{{ summary.violationEventCount }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" class="stat-row">
      <el-col :span="8">
        <el-card shadow="hover" class="stat-item">
          <div class="label">隐患上报</div>
          <div class="value">{{ summary.hazardReportCount }}</div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="stat-item">
          <div class="label">待处理报警</div>
          <div class="value">{{ summary.pendingCount }}</div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="stat-item key-area-card">
          <div class="label">重点区域</div>
          <div class="key-area-name">{{ keyArea.name || '—' }}</div>
          <div class="key-area-meta">
            <span class="key-area-count">{{ keyArea.count }}次</span>
            <span class="key-area-percent">占{{ keyArea.percent }}%</span>
          </div>
          <el-progress
            :percentage="keyArea.percent"
            :show-text="false"
            :stroke-width="8"
            color="#E6A23C"
            class="key-area-progress"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" class="pending-row">
      <el-col :span="8">
        <el-card shadow="hover" class="pending-card pending-card--high">
          <div class="pending-card__head">
            <span class="pending-card__title">紧急报警</span>
            <el-tag type="danger" size="mini" effect="plain">需立即处理</el-tag>
          </div>
          <ul class="pending-list">
            <li v-for="(row, idx) in pendingHigh" :key="'h-' + idx" class="pending-item">
              <el-tag type="danger" size="mini" effect="dark" class="pending-item__tag">{{ row.riskLabel }}</el-tag>
              <div class="pending-item__body">
                <div class="pending-item__text">{{ row.summary || row.alarmType }}</div>
                <div class="pending-item__meta">{{ row.alarmTime }} · {{ row.region }}</div>
              </div>
            </li>
            <li v-if="!pendingHigh.length" class="pending-empty">暂无高危待处理</li>
          </ul>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="pending-card pending-card--medium">
          <div class="pending-card__head">
            <span class="pending-card__title">一般报警</span>
            <el-tag type="warning" size="mini" effect="plain">24小时内处理</el-tag>
          </div>
          <ul class="pending-list">
            <li v-for="(row, idx) in pendingMedium" :key="'m-' + idx" class="pending-item">
              <el-tag type="warning" size="mini" effect="plain" class="pending-item__tag">{{ row.riskLabel }}</el-tag>
              <div class="pending-item__body">
                <div class="pending-item__text">{{ row.summary || row.alarmType }}</div>
                <div class="pending-item__meta">{{ row.alarmTime }} · {{ row.region }}</div>
              </div>
            </li>
            <li v-if="!pendingMedium.length" class="pending-empty">暂无中危待处理</li>
          </ul>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="pending-card pending-card--low">
          <div class="pending-card__head">
            <span class="pending-card__title">提示信息</span>
            <el-tag type="success" size="mini" effect="plain">低优先级</el-tag>
          </div>
          <ul class="pending-list">
            <li v-for="(row, idx) in pendingLow" :key="'l-' + idx" class="pending-item">
              <span class="pending-item__tag tag-low">{{ row.riskLabel }}</span>
              <div class="pending-item__body">
                <div class="pending-item__text">{{ row.summary || row.alarmType }}</div>
                <div class="pending-item__meta">{{ row.alarmTime }} · {{ row.region }}</div>
              </div>
            </li>
            <li v-if="!pendingLow.length" class="pending-empty">暂无低危待处理</li>
          </ul>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="progress-card">
      <div slot="header">处理进度跟踪</div>
      <el-row :gutter="24" type="flex" align="middle" class="progress-inner">
        <el-col :span="8" class="progress-col">
          <div class="progress-label">处理完成率</div>
          <el-progress
            type="circle"
            :percentage="progress.completionRate || 0"
            :width="120"
            :stroke-width="10"
            color="#67C23A"
          />
        </el-col>
        <el-col :span="8" class="progress-col">
          <div class="progress-label">待处理条数</div>
          <div class="progress-metric">{{ summary.pendingCount }}</div>
        </el-col>
        <el-col :span="8" class="progress-col">
          <div class="progress-label">报警总数（本区间）</div>
          <div class="progress-metric progress-metric--muted">{{ progress.totalCount }}</div>
        </el-col>
      </el-row>
    </el-card>

    <el-row :gutter="12" class="chart-row">
      <el-col :span="16">
        <el-card shadow="never" class="chart-card">
          <div slot="header">报警数量趋势</div>
          <div ref="trendChart" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="chart-card">
          <div slot="header">报警类型分布</div>
          <div ref="typeChart" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" class="chart-row">
      <el-col :span="24">
        <el-card shadow="never" class="chart-card">
          <div slot="header">区域报警分布</div>
          <div ref="regionChart" class="chart-box region-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" class="chart-row">
      <el-col :span="24">
        <el-card shadow="never" class="chart-card heat-map-card">
          <div slot="header">热点区域报警密度</div>
          <region-heat-map
            :region-list="regionDistribution"
            :initial-lnglat="mapCenter"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="table-card">
      <div slot="header">报警明细（按时间）</div>
      <el-table :data="alarmRecordList" stripe border height="360" size="small">
        <el-table-column label="报警时间" prop="alarmTime" min-width="160" />
        <el-table-column label="区域位置" prop="region" min-width="140" show-overflow-tooltip />
        <el-table-column label="报警类型" prop="alarmType" min-width="100" />
        <el-table-column label="紧急程度" prop="riskLabel" min-width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.riskTier === 'high'"
              type="danger"
              size="small"
              effect="dark"
            >{{ scope.row.riskLabel }}</el-tag>
            <el-tag
              v-else-if="scope.row.riskTier === 'medium'"
              type="warning"
              size="small"
            >{{ scope.row.riskLabel }}</el-tag>
            <span v-else class="tag-low tag-low--inline">{{ scope.row.riskLabel }}</span>
          </template>
        </el-table-column>
        <el-table-column label="级别明细" prop="levelDetail" min-width="90" align="center" />
        <el-table-column label="报警内容" prop="summary" min-width="200" show-overflow-tooltip />
        <el-table-column label="处理状态" prop="statusText" min-width="90" align="center" />
      </el-table>
    </el-card>

    <el-row :gutter="12" class="rank-row">
      <el-col :span="12">
        <el-card shadow="never" class="rank-card">
          <div slot="header">设备故障分析（工单设备名 Top）</div>
          <div class="rank-list">
            <div v-for="(item, idx) in deviceFaultRanking" :key="'d-' + idx" class="rank-line">
              <span class="rank-name" :title="item.name">{{ item.name }}</span>
              <div class="rank-bar-wrap">
                <div
                  class="rank-bar rank-bar--device"
                  :style="{ width: rankBarPercent(item.value, deviceRankMax) + '%' }"
                />
              </div>
              <span class="rank-count">{{ item.value }}次</span>
            </div>
            <div v-if="!deviceFaultRanking.length" class="rank-empty">暂无数据</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card shadow="never" class="rank-card">
          <div slot="header">安全违规分析（违规类型 Top）</div>
          <div class="rank-list">
            <div v-for="(item, idx) in violationTypeRanking" :key="'v-' + idx" class="rank-line">
              <span class="rank-name" :title="item.name">{{ item.name }}</span>
              <div class="rank-bar-wrap">
                <div
                  class="rank-bar rank-bar--violation"
                  :style="{ width: rankBarPercent(item.value, violationRankMax) + '%' }"
                />
              </div>
              <span class="rank-count">{{ item.value }}次</span>
            </div>
            <div v-if="!violationTypeRanking.length" class="rank-empty">暂无数据</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="table-card">
      <div slot="header">报警明细汇总（日期 / 区域 / 类型 / 数量）</div>
      <el-table :data="detailList" stripe border height="280" size="small">
        <el-table-column label="日期" prop="date" min-width="120" />
        <el-table-column label="区域" prop="region" min-width="160" />
        <el-table-column label="报警类型" prop="alarmType" min-width="120" />
        <el-table-column label="数量" prop="count" min-width="80" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { getDashboardData } from './api';
import RegionHeatMap from './components/region-heat-map.vue';
import { getDefaultSearchTimeRange, getSearchTimePickerOptions } from '@/basic-common/util/common';

export default {
  components: {
    RegionHeatMap
  },
  data() {
    return {
      /** 与 basic-info/personnel-trajectory 地图默认中心一致 */
      mapCenter: [105.272575, 37.627891],
      query: {
        dateRange: [],
        region: '',
        alarmType: '',
        emergencyLevel: 'all',
        granularity: 'day'
      },
      pickerOptions: getSearchTimePickerOptions(),
      summary: {
        totalCount: 0,
        deviceFaultCount: 0,
        violationEventCount: 0,
        hazardReportCount: 0,
        pendingCount: 0
      },
      dateAxis: [],
      trendSeries: [],
      typeDistribution: [],
      regionDistribution: [],
      /** 重点区域：报警次数最多的区域（与区域分布同源） */
      keyArea: {
        name: '',
        count: 0,
        percent: 0
      },
      detailList: [],
      alarmRecordList: [],
      pendingHigh: [],
      pendingMedium: [],
      pendingLow: [],
      progress: {
        completionRate: 0,
        pendingCount: 0,
        totalCount: 0
      },
      deviceFaultRanking: [],
      violationTypeRanking: [],
      regionOptions: [],
      typeOptions: ['设备故障', '违规事件', '隐患上报'],
      trendChartInstance: null,
      typeChartInstance: null,
      regionChartInstance: null
    };
  },
  mounted() {
    this.resetQuery();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.disposeCharts();
  },
  computed: {
    deviceRankMax() {
      return this.rankMaxOf(this.deviceFaultRanking);
    },
    violationRankMax() {
      return this.rankMaxOf(this.violationTypeRanking);
    }
  },
  methods: {
    rankMaxOf(list) {
      if (!list || !list.length) {
        return 1;
      }
      let m = 0;
      list.forEach((i) => {
        const v = Number(i.value) || 0;
        if (v > m) m = v;
      });
      return m || 1;
    },
    rankBarPercent(val, max) {
      const n = Number(val) || 0;
      const d = max || 1;
      return Math.min(100, Math.round((n / d) * 100));
    },
    resetQuery() {
      this.query = {
        dateRange: getDefaultSearchTimeRange(),
        region: '',
        alarmType: '',
        emergencyLevel: 'all',
        granularity: 'day'
      };
      this.loadData();
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = `${date.getMonth() + 1}`.padStart(2, '0');
      const d = `${date.getDate()}`.padStart(2, '0');
      const h = `${date.getHours()}`.padStart(2, '0');
      const mm = `${date.getMinutes()}`.padStart(2, '0');
      const s = `${date.getSeconds()}`.padStart(2, '0');
      return `${y}-${m}-${d} ${h}:${mm}:${s}`;
    },
    loadData() {
      const params = {
        startTime: this.query.dateRange && this.query.dateRange[0] ? this.query.dateRange[0] : null,
        endTime: this.query.dateRange && this.query.dateRange[1] ? this.query.dateRange[1] : null,
        region: this.query.region,
        alarmType: this.query.alarmType,
        emergencyLevel: this.query.emergencyLevel === 'all' ? '' : this.query.emergencyLevel,
        granularity: this.query.granularity
      };
      getDashboardData(params).then(({ data }) => {
        const res = data.data || {};
        this.summary = res.summary || this.summary;
        this.dateAxis = res.dateAxis || [];
        this.trendSeries = res.trendSeries || [];
        this.typeDistribution = res.typeDistribution || [];
        this.regionDistribution = res.regionDistribution || [];
        this.keyArea = res.keyArea || { name: '', count: 0, percent: 0 };
        this.detailList = res.detailList || [];
        this.alarmRecordList = res.alarmRecordList || [];
        this.pendingHigh = res.pendingHigh || [];
        this.pendingMedium = res.pendingMedium || [];
        this.pendingLow = res.pendingLow || [];
        this.progress = res.progress || { completionRate: 0, pendingCount: 0, totalCount: 0 };
        this.deviceFaultRanking = res.deviceFaultRanking || [];
        this.violationTypeRanking = res.violationTypeRanking || [];
        this.regionOptions = res.regionOptions || [];
        this.typeOptions = res.typeOptions || this.typeOptions;
        this.renderCharts();
      });
    },
    renderCharts() {
      this.renderTrendChart();
      this.renderTypeChart();
      this.renderRegionChart();
    },
    renderTrendChart() {
      if (!this.trendChartInstance) {
        this.trendChartInstance = echarts.init(this.$refs.trendChart);
      }
      const option = {
        tooltip: { trigger: 'axis' },
        legend: { top: 10 },
        grid: { left: 40, right: 20, top: 50, bottom: 30 },
        xAxis: { type: 'category', data: this.dateAxis },
        yAxis: { type: 'value', name: '数量' },
        series: (this.trendSeries || []).map((item) => ({
          name: item.name,
          type: 'line',
          smooth: true,
          data: item.data || []
        }))
      };
      this.trendChartInstance.setOption(option, true);
    },
    renderTypeChart() {
      if (!this.typeChartInstance) {
        this.typeChartInstance = echarts.init(this.$refs.typeChart);
      }
      const option = {
        tooltip: { trigger: 'item' },
        legend: { bottom: 0 },
        series: [
          {
            type: 'pie',
            radius: ['45%', '70%'],
            avoidLabelOverlap: false,
            data: this.typeDistribution
          }
        ]
      };
      this.typeChartInstance.setOption(option, true);
    },
    renderRegionChart() {
      if (!this.regionChartInstance) {
        this.regionChartInstance = echarts.init(this.$refs.regionChart);
      }
      const option = {
        tooltip: { trigger: 'axis' },
        grid: { left: 60, right: 20, top: 20, bottom: 40 },
        xAxis: {
          type: 'category',
          axisLabel: { interval: 0, rotate: 20 },
          data: this.regionDistribution.map((item) => item.name)
        },
        yAxis: { type: 'value', name: '数量' },
        series: [
          {
            type: 'bar',
            barMaxWidth: 36,
            data: this.regionDistribution.map((item) => item.value)
          }
        ]
      };
      this.regionChartInstance.setOption(option, true);
    },
    handleResize() {
      if (this.trendChartInstance) this.trendChartInstance.resize();
      if (this.typeChartInstance) this.typeChartInstance.resize();
      if (this.regionChartInstance) this.regionChartInstance.resize();
    },
    disposeCharts() {
      if (this.trendChartInstance) this.trendChartInstance.dispose();
      if (this.typeChartInstance) this.typeChartInstance.dispose();
      if (this.regionChartInstance) this.regionChartInstance.dispose();
      this.trendChartInstance = null;
      this.typeChartInstance = null;
      this.regionChartInstance = null;
    }
  }
};
</script>

<style scoped lang="scss">
.alarm-analysis-page {
  background: #f7f8fa;
  padding: 10px;
}

.filter-card,
.table-card,
.chart-card {
  margin-bottom: 12px;
}

.stat-row {
  margin-bottom: 12px;
}

.stat-item {
  .label {
    color: #909399;
    font-size: 13px;
  }
  .value {
    margin-top: 8px;
    font-size: 28px;
    color: #303133;
    font-weight: 600;
  }
}

.key-area-card {
  .key-area-name {
    margin-top: 6px;
    font-size: 16px;
    color: #303133;
    font-weight: 600;
    min-height: 24px;
  }
  .key-area-meta {
    margin-top: 8px;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .key-area-count {
    font-size: 22px;
    color: #303133;
    font-weight: 600;
  }
  .key-area-percent {
    font-size: 13px;
    color: #909399;
  }
  .key-area-progress {
    margin-top: 10px;
  }
}

.chart-row {
  margin-bottom: 12px;
}

.chart-box {
  width: 100%;
  height: 320px;
}

.region-box {
  height: 280px;
}

.heat-map-card {
  margin-bottom: 12px;
}

.pending-row {
  margin-bottom: 12px;
}

.pending-card {
  min-height: 280px;
  border-radius: 8px;
  &--high {
    border-top: 3px solid #f56c6c;
  }
  &--medium {
    border-top: 3px solid #e6a23c;
  }
  &--low {
    border-top: 3px solid #67c23a;
  }
}

.pending-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.pending-card__title {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
}

.pending-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
}

.pending-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
  font-size: 13px;
}

.pending-item__tag {
  flex-shrink: 0;
}

.pending-item__body {
  flex: 1;
  min-width: 0;
}

.pending-item__text {
  color: #303133;
  line-height: 1.4;
  word-break: break-all;
}

.pending-item__meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.pending-empty {
  padding: 24px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

.tag-low {
  display: inline-block;
  padding: 0 8px;
  height: 24px;
  line-height: 22px;
  font-size: 12px;
  border-radius: 4px;
  border: 1px solid #13c2c2;
  color: #08979c;
  background: #e6fffb;
  flex-shrink: 0;
}

.tag-low--inline {
  vertical-align: middle;
}

.progress-card {
  margin-bottom: 12px;
}

.progress-inner {
  padding: 8px 0 16px;
}

.progress-col {
  text-align: center;
}

.progress-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 12px;
}

.progress-metric {
  font-size: 36px;
  font-weight: 600;
  color: #f56c6c;
  line-height: 120px;
  &--muted {
    color: #303133;
  }
}

.rank-row {
  margin-bottom: 12px;
}

.rank-card {
  min-height: 260px;
}

.rank-list {
  padding: 4px 0;
}

.rank-line {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 13px;
}

.rank-name {
  width: 120px;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #606266;
}

.rank-bar-wrap {
  flex: 1;
  height: 10px;
  background: #f0f2f5;
  border-radius: 5px;
  overflow: hidden;
}

.rank-bar {
  height: 100%;
  border-radius: 5px;
  transition: width 0.3s ease;
  &--device {
    background: linear-gradient(90deg, #409eff, #79bbff);
  }
  &--violation {
    background: linear-gradient(90deg, #67c23a, #95d475);
  }
}

.rank-count {
  width: 48px;
  flex-shrink: 0;
  text-align: right;
  color: #303133;
  font-weight: 500;
}

.rank-empty {
  text-align: center;
  color: #c0c4cc;
  padding: 40px 0;
  font-size: 13px;
}
</style>

